import * as React from "react";
import { ScrollArea as ScrollAreaPrimitive } from "radix-ui";

import { cn } from "../lib/utils";

function ScrollArea({
  className,
  viewportClassName,
  constrainWidth = false,
  children,
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  /** Extra classes for the Radix Viewport. A `max-h-*` sizing constraint MUST go here,
   * not on the root: the viewport's `size-full` (height: 100%) resolves to `auto`
   * against a root whose height is only capped by max-height, so the viewport grows
   * with its content and never scrolls — the root's overflow-hidden then clips the
   * excess with no scroll path to reach it.
   *
   * The same trap applies to a root sized by `flex-1 min-h-0` when the FLEX CONTAINER
   * itself has only a max-height (e.g. a `max-h-[85vh]` dialog): per the flex spec the
   * container's height is indefinite, so the item's flexed height is too, and the
   * viewport's 100% again resolves to auto (content clips, wheel scroll dead, while
   * programmatic scrollIntoView still works — which is why E2E clicks won't catch it).
   * In that layout use CSS GRID on the container instead
   * (`grid grid-rows-[auto_minmax(0,1fr)_auto]`): grid areas are definite by spec, so
   * the viewport's percentage height resolves. Roots inside a definite-height parent
   * (a Sheet, an explicit h-*) are fine with flex. */
  viewportClassName?: string;
  /** Force the viewport's content wrapper to block layout. Radix renders a
   * `display:table` wrapper that GROWS to fit wide children instead of
   * constraining them, so a child's own `overflow-x-auto` never engages and the
   * root's overflow-hidden clips the excess sideways with no scroll path — e.g.
   * a wide grid in a dialog losing its last column. With `constrainWidth`,
   * content stays width-bound and wide children scroll in their own containers
   * (WCAG 1.4.10 reflow). Opt-in because table growth is Radix's default sizing
   * behavior and existing consumers keep it. */
  constrainWidth?: boolean;
}) {
  const warnedRootMaxH = React.useRef(false);
  if (
    process.env.NODE_ENV !== "production" &&
    !warnedRootMaxH.current &&
    className &&
    /(^|[\s:!])max-h-/.test(className)
  ) {
    warnedRootMaxH.current = true;
    // eslint-disable-next-line no-console
    console.warn(
      "ScrollArea: a max-h on the root is clipped, not scrolled (the viewport ignores it) — pass it via viewportClassName instead."
    );
  }
  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      // overflow-hidden clips content to the root (upstream shadcn default): without
      // it, content taller than the viewport paints over siblings below — e.g. over a
      // dialog's sticky footer, visually burying its buttons and stealing their clicks.
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        data-slot="scroll-area-viewport"
        className={cn(
          "focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background size-full rounded-[inherit] outline-none focus-visible:ring-2",
          // !important outranks the wrapper's inline display:table style.
          constrainWidth && "[&>div]:!block",
          viewportClassName
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        "flex touch-none select-none p-px transition-colors",
        orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent",
        orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent",
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="bg-border relative flex-1 rounded-full"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
}

export { ScrollArea, ScrollBar };
